import { IKuikaDataType, IKuikaDataTypeField, IWFParameter, IWorkFlowBaseInfo } from "@kuika/kuika-cl-model";

/**
 * Pure reference bookkeeping for the Kuika Data Types manager (KOA-18242 delete protection,
 * KOA-18267 rename cascade). Catalog references are held by NAME string — a type's field points at
 * another type via `field.type`, a workflow parameter via `parameter.dataTypeName` — so the audit
 * and the rename plumbing here must stay in lockstep with the backend's cascade
 * (`KuikaDataTypeCatalog.RenameElementReferences`, `WorkflowParameterTypeReferences`).
 */

/** One row of the manager's editor: the current type plus the name it had when loaded (for rename/delete diffing). */
export interface CatalogEntry {
  originalName?: string;
  dataType: IKuikaDataType;
}

/** Where a catalog type is still referenced from — a non-empty audit blocks its deletion. */
export interface TypeUsage {
  /** Other catalog types whose fields reference this one (read LIVE from the editor). */
  referencingTypes: string[];
  /** Workflows whose parameters reference this one (read from the saved project workflows). */
  referencingWorkflows: string[];
}

/** One type rename applied by a save: the persisted name it had, and the name it has now. */
export interface IDataTypeRename {
  from: string;
  to: string;
}

/** A normalized key for comparing type names — trimmed + lowercased, mirroring the backend's case-insensitive match. */
export const typeNameKey = (name: string | undefined): string => (name ?? "").trim().toLowerCase();

/**
 * Reference audit for one catalog entry, used to disable its delete affordance. Type→type references
 * are read from the LIVE editor (`entries`) and match the entry's live name OR its SAVED name
 * (`originalName`): a field still holding the saved name keeps counting as a reference through an
 * in-session rename, because saving cascades that field to the new name rather than dropping it —
 * without the saved-name match, rename-then-delete would bypass the protection (KOA-18267).
 * Workflow references are matched against the SAVED name only — the name the persisted workflows
 * actually point at — so an in-session rename neither masks nor invents one (a brand-new,
 * never-saved type has no `originalName`, so no workflow can reference it yet).
 */
export const collectTypeUsage = (
  entry: CatalogEntry,
  index: number,
  entries: CatalogEntry[],
  workflows: IWorkFlowBaseInfo[]
): TypeUsage => {
  const liveKey: string = typeNameKey(entry.dataType.name);
  const savedKey: string = typeNameKey(entry.originalName);

  const referencingTypes: string[] = [];
  const typeKeys: Set<string> = new Set<string>([liveKey, savedKey].filter((key: string): boolean => !!key));
  if (typeKeys.size > 0) {
    entries.forEach((other: CatalogEntry, i: number): void => {
      if (i === index) return;
      const fields: IKuikaDataTypeField[] = other.dataType.elements ?? [];
      const referencesTarget: boolean = fields.some((field: IKuikaDataTypeField): boolean =>
        typeKeys.has(typeNameKey(field.type))
      );
      if (referencesTarget && other.dataType.name) referencingTypes.push(other.dataType.name);
    });
  }

  const referencingWorkflows: string[] = [];
  if (savedKey) {
    workflows.forEach((workflow: IWorkFlowBaseInfo): void => {
      const parameters: IWFParameter[] = workflow.parameters ?? [];
      const referencesTarget: boolean = parameters.some(
        (parameter: IWFParameter): boolean => !!parameter.isObject && typeNameKey(parameter.dataTypeName) === savedKey
      );
      if (referencesTarget) referencingWorkflows.push(workflow.name || "Untitled workflow");
    });
  }

  return { referencingTypes, referencingWorkflows };
};

/** The tooltip / aria copy for a blocked delete, or null when the type is free to remove. */
export const describeTypeUsage = (usage: TypeUsage): string | null => {
  const clauses: string[] = [];
  if (usage.referencingTypes.length > 0) {
    clauses.push(`the type ${usage.referencingTypes.map((n: string): string => `"${n}"`).join(", ")}`);
  }
  if (usage.referencingWorkflows.length > 0) {
    const uniqueWorkflows: string[] = [...new Set<string>(usage.referencingWorkflows)];
    clauses.push(`the workflow ${uniqueWorkflows.map((n: string): string => `"${n}"`).join(", ")}`);
  }
  if (clauses.length === 0) return null;
  return `Can't delete — this type is used by ${clauses.join(" and ")}. Remove the reference first.`;
};
